import { motion } from 'framer-motion';
import { CheckCircle2, Circle, Flag, Award } from 'lucide-react';
import { fmtDate, daysUntil } from '../../lib/planConstants';

export default function PlanTimeline({ plan, tasks, onOpenTask }) {
  const phases = plan.phases || [];
  const milestones = plan.milestones || [];

  return (
    <div className="space-y-5" data-testid="plan-timeline">
      {phases.map((phase, pi) => {
        const phaseMilestones = milestones.filter((m) => m.phase_key === phase.key);
        const phaseTasks = tasks.filter((t) => t.phase_key === phase.key && !t.archived);
        const done = phaseTasks.filter((t) => t.status === 'completed').length;
        const pct = phaseTasks.length ? Math.round((done / phaseTasks.length) * 100) : 0;
        return (
          <motion.div key={phase.key || pi} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: pi * 0.05 }} className="glass-card p-5" data-testid={`phase-${phase.key}`}>
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-[11px] uppercase tracking-[0.2em] text-cyan-300 font-semibold">Phase {pi + 1}</span>
              <h3 className="text-base md:text-lg font-semibold text-white">{phase.name}</h3>
              <span className="text-xs text-slate-300">{fmtDate(phase.start_date)} → {fmtDate(phase.end_date)}</span>
              <span className="ml-auto text-xs text-slate-300">{done}/{phaseTasks.length} tasks · {pct}%</span>
            </div>
            {phase.focus && <p className="text-sm text-slate-300 mt-1">{phase.focus}</p>}
            <div className="h-1.5 rounded-full bg-slate-900 overflow-hidden mt-3">
              <motion.div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.8 }} />
            </div>

            <div className="mt-4 space-y-3">
              {phaseMilestones.map((m) => {
                const mTasks = phaseTasks.filter((t) => t.milestone_id === m.id);
                const mDone = mTasks.filter((t) => t.status === 'completed').length;
                const complete = m.status === 'completed';
                const dLeft = daysUntil(m.target_date);
                return (
                  <div key={m.id} className={`rounded-xl border p-3.5 ${complete ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-slate-700/80 bg-slate-900/40'}`}
                    data-testid={`milestone-${m.id}`}>
                    <div className="flex items-start gap-2.5">
                      {complete ? <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5" /> : <Flag className="w-4 h-4 text-cyan-300 mt-0.5" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-white">{m.title}</p>
                        <p className="text-xs text-slate-300 mt-0.5">{m.description}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-1.5 text-[11px]">
                          <span className="text-slate-300">{fmtDate(m.target_date)}</span>
                          {!complete && dLeft != null && (
                            <span className={dLeft < 0 ? 'text-rose-300' : 'text-slate-300'}>
                              {dLeft < 0 ? `${Math.abs(dLeft)} days late` : `${dLeft} days left`}
                            </span>
                          )}
                          <span className="text-slate-300">{mDone}/{mTasks.length} tasks</span>
                          {m.is_checkpoint && <span className="text-violet-300">checkpoint</span>}
                          {m.review_by && m.review_by !== 'none' && <span className="text-violet-300">{m.review_by} review</span>}
                          {m.badge?.name && (
                            <span className={`inline-flex items-center gap-1 ${complete ? 'text-amber-300' : 'text-slate-300'}`}>
                              <Award className="w-3 h-3" />{m.badge.name}
                            </span>
                          )}
                        </div>
                        {m.completion_criteria && <p className="text-[11px] text-slate-300 mt-1.5">Done when: {m.completion_criteria}</p>}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {mTasks.map((t) => (
                            <button key={t.id} onClick={() => onOpenTask(t)} data-testid={`timeline-task-${t.id}`}
                              className={`text-[11px] px-2 py-1 rounded-lg border transition-colors duration-200 ${
                                t.status === 'completed'
                                  ? 'border-emerald-500/30 text-emerald-300/80 line-through'
                                  : 'border-slate-700 text-slate-300 hover:border-cyan-500/50'}`}>
                              {t.status === 'completed' ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <Circle className="w-3 h-3 inline mr-1" />}
                              {t.title}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
